import React, { useState, useEffect } from 'react';
import { amplifyApi } from '../../services/amplifyApi';
import { Loader2, Sparkles, Users, Calendar, TrendingUp, RefreshCw, AlertTriangle, Shield } from 'lucide-react';

// Current rate limits (should match backend db.js AI_LIMITS)
const RATE_LIMITS = {
  generation: { hourly: 5, daily: 20 },
  bgRemoval: { hourly: 20, daily: 50 }
};

function AiStatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await amplifyApi.getAdminAiStats();
      if (response.success) {
        setStats(response);
      } else {
        setError(response.error || 'Failed to load AI stats');
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadStats}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header with Refresh */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-500" />
          AI Generation Stats
        </h2>
        <button
          onClick={loadStats}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Current Rate Limits Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-blue-800">Current Rate Limits</h3>
            <div className="mt-2 grid grid-cols-2 gap-4 text-sm text-blue-700">
              <div>
                <span className="font-medium">AI Generation/Edit:</span>
                <span className="ml-2">{RATE_LIMITS.generation.hourly}/hour, {RATE_LIMITS.generation.daily}/day</span>
              </div>
              <div>
                <span className="font-medium">Background Removal:</span>
                <span className="ml-2">{RATE_LIMITS.bgRemoval.hourly}/hour, {RATE_LIMITS.bgRemoval.daily}/day</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl p-5 text-white">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 opacity-80" />
            <span className="text-sm font-medium opacity-90">Today</span>
          </div>
          <p className="text-3xl font-bold">{stats?.today?.generations || 0}</p>
          <p className="text-sm opacity-80 mt-1">
            {stats?.today?.uniqueUsers || 0} unique users
          </p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl p-5 text-white">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5 opacity-80" />
            <span className="text-sm font-medium opacity-90">This Month</span>
          </div>
          <p className="text-3xl font-bold">{stats?.thisMonth?.generations || 0}</p>
          <p className="text-sm opacity-80 mt-1">
            {stats?.thisMonth?.uniqueUsers || 0} unique users
          </p>
        </div>

        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-5 text-white">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 opacity-80" />
            <span className="text-sm font-medium opacity-90">Avg/Day (7d)</span>
          </div>
          <p className="text-3xl font-bold">
            {stats?.daily?.length > 0
              ? Math.round(stats.daily.reduce((sum, d) => sum + (d.generations || 0), 0) / stats.daily.length)
              : 0}
          </p>
          <p className="text-sm opacity-80 mt-1">generations</p>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-5 text-white">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 opacity-80" />
            <span className="text-sm font-medium opacity-90">Active Users</span>
          </div>
          <p className="text-3xl font-bold">{stats?.topUsers?.length || 0}</p>
          <p className="text-sm opacity-80 mt-1">with AI usage</p>
        </div>
      </div>

      {/* Daily Stats Chart */}
      <div className="bg-slate-50 rounded-xl p-5 mb-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Last 7 Days</h3>
        <div className="flex items-end gap-2 h-32">
          {(stats?.daily || []).slice().reverse().map((day) => {
            const maxGen = Math.max(...(stats?.daily || []).map(d => d.generations || 1));
            const height = ((day.generations || 0) / maxGen) * 100;
            return (
              <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs font-medium text-slate-600">{day.generations || 0}</span>
                <div
                  className="w-full bg-gradient-to-t from-purple-500 to-indigo-400 rounded-t-md transition-all"
                  style={{ height: `${Math.max(height, 4)}%` }}
                ></div>
                <span className="text-xs text-slate-500">
                  {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Monthly Stats */}
      <div className="bg-slate-50 rounded-xl p-5 mb-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Monthly Trend</h3>
        <div className="grid grid-cols-6 gap-3">
          {(stats?.monthly || []).slice().reverse().map((month) => (
            <div key={month.month} className="text-center p-3 bg-white rounded-lg border border-slate-200">
              <p className="text-lg font-bold text-slate-800">{month.generations || 0}</p>
              <p className="text-xs text-slate-500 mt-1">
                {new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
              </p>
              <p className="text-xs text-slate-400">{month.uniqueUsers || 0} users</p>
            </div>
          ))}
        </div>
      </div>

      {/* User Stats Table */}
      <div className="bg-slate-50 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-700">User AI Usage (Top 50)</h3>
          <span className="text-xs text-slate-500">
            {stats?.topUsers?.length || 0} users tracked
          </span>
        </div>
        {stats?.topUsers?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <th className="pb-3 pr-4">#</th>
                  <th className="pb-3 pr-4">User</th>
                  <th className="pb-3 pr-4 text-center">Total Gens</th>
                  <th className="pb-3 pr-4 text-center">Status</th>
                  <th className="pb-3 text-right">Last Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {stats.topUsers.map((user, idx) => {
                  // Flag high usage users (>80% of daily limit)
                  const isHighUsage = user.totalGenerations >= RATE_LIMITS.generation.daily * 0.8;
                  return (
                    <tr key={user.userId} className={`text-sm ${isHighUsage ? 'bg-amber-50' : ''}`}>
                      <td className="py-3 pr-4 text-slate-500">{idx + 1}</td>
                      <td className="py-3 pr-4">
                        <div>
                          {user.email ? (
                            <span className="text-slate-800">{user.email}</span>
                          ) : (
                            <span className="font-mono text-xs text-slate-500">
                              {user.userId.slice(0, 8)}...{user.userId.slice(-4)}
                            </span>
                          )}
                        </div>
                        <div className="font-mono text-xs text-slate-400 mt-0.5">
                          {user.userId.slice(0, 12)}...
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <span className={`font-semibold ${isHighUsage ? 'text-amber-700' : 'text-slate-800'}`}>
                          {user.totalGenerations}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-center">
                        {isHighUsage ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                            <AlertTriangle className="w-3 h-3" />
                            High Usage
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            Normal
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-right text-slate-500">
                        {user.lastGenerationAt
                          ? new Date(user.lastGenerationAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-4">No AI usage data yet</p>
        )}
      </div>
    </div>
  );
}

export default AiStatsTab;
